import { useState, useEffect } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailRouter } from "../lib/router-context";
import { Sparkles, Tag, Calendar, Loader2, ChevronLeft } from "lucide-react";
import { ActuDetailPanel } from "../components/ActuDetailPanel";
import type { ActualiteUnified } from "../components/ActuDetailPanel";

function formatDate(d?: string | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}
function fmtCurrency(v: string | null | number) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  return isNaN(n) ? "—" : n.toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 });
}

type FilterType = "all" | "nouveaute" | "promotion" | "information";

const FILTER_OPTIONS: { value: FilterType; label: string }[] = [
  { value: "all",        label: "Toutes" },
  { value: "nouveaute",  label: "Nouveautés" },
  { value: "promotion",  label: "Promotions" },
  { value: "information",label: "Informations" },
];

export default function ActualitesPage() {
  const { navigate } = usePortailRouter();
  const [actualites, setActualites] = useState<ActualiteUnified[]>([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState<FilterType>("all");
  const [selected, setSelected]     = useState<ActualiteUnified | null>(null);

  useEffect(() => {
    setLoading(true);
    authFetch("/api/portail/actualites")
      .then(r => r.json())
      .then(data => setActualites(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? actualites : actualites.filter(a => a.type === filter);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">

        {/* ── En-tête ─────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            data-testid="btn-retour-dashboard"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Actualités
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Toutes les nouveautés, promotions et informations de votre espace partenaire
            </p>
          </div>
        </div>

        {/* ── Filtres ─────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              data-testid={`filter-${opt.value}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                filter === opt.value
                  ? "bg-primary text-white border-primary"
                  : "border-border text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* ── Grille ──────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center h-48 gap-2 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Chargement...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-muted/30 rounded-xl p-10 text-center text-sm text-muted-foreground">
            Aucune actualité pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(actu => {
              const isPromo = actu.type === "promotion";
              const isInfo  = actu.type === "information";
              const badge = isPromo
                ? { label: "Promotion",   cls: "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400" }
                : isInfo
                ? { label: "Information", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" }
                : { label: "Nouveauté",   cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400" };
              const displayTitle = isPromo ? (actu.produitNom || actu.titre) : actu.titre;
              const economie = isPromo && actu.prixNormal && actu.prixPromo
                ? parseFloat(actu.prixNormal) - parseFloat(actu.prixPromo)
                : null;

              return (
                <div
                  key={actu.id}
                  className="bg-card border border-border/50 rounded-xl overflow-hidden flex flex-col hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelected(actu)}
                  data-testid={`card-actualite-${actu.id}`}
                >
                  {actu.imageUrl ? (
                    <img src={actu.imageUrl} alt={displayTitle} className="w-full h-36 object-cover" />
                  ) : (
                    <div className="w-full h-36 flex items-center justify-center bg-muted/40">
                      {isPromo
                        ? <Tag className="w-8 h-8 text-muted-foreground/30" />
                        : <Sparkles className="w-8 h-8 text-muted-foreground/30" />}
                    </div>
                  )}
                  <div className="p-3 flex flex-col flex-1 gap-2">
                    <span className={`self-start text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${badge.cls}`}>
                      {badge.label}
                    </span>
                    <p className="font-semibold text-sm leading-snug line-clamp-2">{displayTitle}</p>
                    {actu.accroche && (
                      <p className="text-xs text-muted-foreground line-clamp-2 flex-1">{actu.accroche}</p>
                    )}
                    {isPromo && (actu.prixNormal || actu.prixPromo) && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {actu.prixNormal && (
                          <span className="text-sm text-muted-foreground line-through">{fmtCurrency(actu.prixNormal)}</span>
                        )}
                        {actu.prixPromo && (
                          <span className="text-base font-bold text-orange-600 dark:text-orange-400">{fmtCurrency(actu.prixPromo)}</span>
                        )}
                        {economie !== null && economie > 0 && (
                          <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 px-1.5 py-0.5 rounded-full font-medium">
                            -{fmtCurrency(economie)}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1 mt-auto">
                      <span className="text-xs text-muted-foreground">{formatDate(actu.createdAt)}</span>
                      {actu.dateFin && isPromo && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Jusqu'au {new Date(actu.dateFin).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selected && <ActuDetailPanel actu={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
